/**
 * JobQueuePanel
 *
 * Displays job queue with tabs for running/pending/history.
 * Composes EITELKEIT atoms and molecules for BRUTAL CI/CD dashboard.
 */

import React from 'react';
import type { CSSProperties } from 'react';
import { Badge } from '@ekelhaft-tools/eitelkeit/atoms';
import { Card, Tabs } from '@ekelhaft-tools/eitelkeit/molecules';
import { tokens } from '@ekelhaft-tools/eitelkeit/tokens';
import type { JobRecord } from '../../types.js';
import { JobCard } from './JobCard.js';

export interface JobQueuePanelProps {
  jobs: JobRecord[];
  onJobClick: ((jobId: string) => void) | null;
  style: CSSProperties | null;
}

type TabValue = 'running' | 'pending' | 'history';

/**
 * Filter jobs by tab category
 */
function filterJobsByTab(jobs: JobRecord[], tab: TabValue): JobRecord[] {
  switch (tab) {
    case 'running':
      return jobs.filter((j) => j.status === 'running');
    case 'pending':
      return jobs.filter((j) => j.status === 'pending');
    case 'history':
      return jobs.filter(
        (j) => j.status === 'completed' || j.status === 'failed' || j.status === 'cancelled',
      );
    default:
      return [];
  }
}

/**
 * Get counts for each tab
 */
function getTabCounts(jobs: JobRecord[]): Record<TabValue, number> {
  return {
    running: jobs.filter((j) => j.status === 'running').length,
    pending: jobs.filter((j) => j.status === 'pending').length,
    history: jobs.filter(
      (j) => j.status === 'completed' || j.status === 'failed' || j.status === 'cancelled',
    ).length,
  };
}

/**
 * Sort jobs by relevance (running first by progress, then by created date)
 */
function sortJobs(jobs: JobRecord[], tab: TabValue): JobRecord[] {
  return [...jobs].sort((a, b) => {
    // For running jobs, sort by progress descending
    if (tab === 'running') {
      return b.progress - a.progress;
    }
    // For history, sort by most recent first
    if (tab === 'history') {
      const aTime = a.completedAt ?? a.failedAt ?? a.createdAt;
      const bTime = b.completedAt ?? b.failedAt ?? b.createdAt;
      return new Date(bTime).getTime() - new Date(aTime).getTime();
    }
    // For pending, sort by created date (oldest first - FIFO)
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });
}

export function JobQueuePanel({ jobs, onJobClick, style }: JobQueuePanelProps) {
  const [activeTab, setActiveTab] = React.useState<TabValue>('running');

  const counts = getTabCounts(jobs);
  const filteredJobs = filterJobsByTab(jobs, activeTab);
  const sortedJobs = sortJobs(filteredJobs, activeTab);

  // Auto-switch to pending if no running jobs but pending jobs exist
  React.useEffect(() => {
    if (counts.running === 0 && counts.pending > 0 && activeTab === 'running') {
      setActiveTab('pending');
    }
  }, [counts.running, counts.pending, activeTab]);

  const containerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[4],
    ...style,
  };

  const headerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing[3],
  };

  const titleStyle: CSSProperties = {
    fontFamily: tokens.fonts.mono,
    fontSize: tokens.fontSizes.lg,
    fontWeight: tokens.fontWeights.semibold,
    color: tokens.colors.textPrimary,
    margin: 0,
  };

  const summaryStyle: CSSProperties = {
    fontFamily: tokens.fonts.mono,
    fontSize: tokens.fontSizes.sm,
    color: tokens.colors.textMuted,
  };

  const tabCountStyle: CSSProperties = {
    marginLeft: tokens.spacing[2],
  };

  const jobListStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[2],
    maxHeight: '400px',
    overflowY: 'auto',
  };

  const emptyStateStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing[6],
    fontFamily: tokens.fonts.sans,
    fontSize: tokens.fontSizes.sm,
    color: tokens.colors.textMuted,
  };

  const getEmptyMessage = (): string => {
    switch (activeTab) {
      case 'running':
        return 'No running jobs';
      case 'pending':
        return 'No pending jobs';
      case 'history':
        return 'No job history';
      default:
        return 'No jobs';
    }
  };

  return (
    <Card variant="elevated" padding="lg" hover={null} style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h2 style={titleStyle}>Job Queue</h2>
        <span style={summaryStyle}>{jobs.length} total jobs</span>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        defaultValue={null}
        onChange={(value) => setActiveTab(value as TabValue)}
        aria-label="Job queue tabs"
      >
        <Tabs.List aria-label="Job status categories">
          <Tabs.Tab value="running">
            Running
            <Badge variant={counts.running > 0 ? 'info' : 'muted'} size="sm" style={tabCountStyle}>
              {counts.running}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="pending">
            Pending
            <Badge variant={counts.pending > 0 ? 'mint' : 'muted'} size="sm" style={tabCountStyle}>
              {counts.pending}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="history">
            History
            <Badge variant="muted" size="sm" style={tabCountStyle}>
              {counts.history}
            </Badge>
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="running">
          {sortedJobs.length > 0 ? (
            <div style={jobListStyle}>
              {sortedJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  onClick={onJobClick ? () => onJobClick(job.id) : null}
                  style={null}
                />
              ))}
            </div>
          ) : (
            <div style={emptyStateStyle}>{getEmptyMessage()}</div>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="pending">
          {sortedJobs.length > 0 ? (
            <div style={jobListStyle}>
              {sortedJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  onClick={onJobClick ? () => onJobClick(job.id) : null}
                  style={null}
                />
              ))}
            </div>
          ) : (
            <div style={emptyStateStyle}>{getEmptyMessage()}</div>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="history">
          {sortedJobs.length > 0 ? (
            <div style={jobListStyle}>
              {sortedJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  onClick={onJobClick ? () => onJobClick(job.id) : null}
                  style={null}
                />
              ))}
            </div>
          ) : (
            <div style={emptyStateStyle}>{getEmptyMessage()}</div>
          )}
        </Tabs.Panel>
      </Tabs>
    </Card>
  );
}
